import {
    Anchor,
    Box,
    Heading,
    Image,
    Paragraph,
    Tag,
    TAG_APPEARANCES,
    Text,
} from '@zillow/constellation';
import { Source, withPreload } from '@zillow/zrs-cms';
import { uniqBy } from 'lodash';
import React from 'react';
import styled from 'styled-components';

import { BaseTerm } from '../../../types/shared';
import { wrap } from '../../../util/wrap';

import { HeroProps, TagAppearance } from './Hero.types';

const isGray = (color: string): boolean =>
    ['gray', 'closeable', 'transparent'].includes(color);

const TAG_APPEARANCES_ARRAY = Object.values(TAG_APPEARANCES).filter(
    (color: string) => !isGray(color)
) as TagAppearance[];

const StyledAnchor = styled(Anchor)`
    text-decoration: none;
`;

const ImagePreload = withPreload(Image);
const SourcePreload = withPreload(Source);

const Hero: React.FC<HeroProps> = ({ post }) => {
    const excerpt = post.excerpt?.raw?.replace(/(<([^>]+)>)/gi, '') || '';

    const categories = uniqBy(
        [post.primaryCategory, ...post.categories],
        'id'
    ).filter((category): category is BaseTerm => Boolean(category));

    const mediumImage = post.featuredImage?.mediaDetails?.sizes?.medium;
    const largeImage = post.featuredImage?.mediaDetails?.sizes?.large;
    // Sometimes mediumImage and largeImage are null, but fullImage is
    // available. In this case, fullImage is used.
    const fullImage = post.featuredImage
        ? {
              sourceUrl: post.featuredImage.sourceUrl,
              width: post.featuredImage.mediaDetails.width,
              height: post.featuredImage.mediaDetails.height,
          }
        : null;

    return (
        <React.Fragment>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '$spacing.xs',
                    justifyContent: 'flex-start',
                    marginBottom: '$spacing.sm',
                    '@media $lg_gte': {
                        gap: '$spacing.sm',
                    },
                    '@media $xxl_gte': {
                        justifyContent: 'center',
                        marginBottom: '$spacing.lg',
                        textAlign: 'center',
                    },
                }}
            >
                {categories?.map((category, index) => (
                    <StyledAnchor key={category.id} href={category.link}>
                        <Tag
                            textTransform="none"
                            appearance={
                                TAG_APPEARANCES_ARRAY[
                                    wrap(
                                        0,
                                        TAG_APPEARANCES_ARRAY.length - 1,
                                        index
                                    )
                                ]
                            }
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: category.name,
                                }}
                            />
                        </Tag>
                    </StyledAnchor>
                ))}

                <Tag textTransform="none" appearance="gray">
                    {parseInt(post.readTime.toString()) || 5} min read
                </Tag>
            </Box>

            <Box
                sx={{
                    '@media $xxl': {
                        textAlign: 'center',
                        textWrap: 'balance',
                    },
                }}
            >
                <Heading
                    level={1}
                    fontType={{
                        default: 'h4',
                        lg: 'h3',
                    }}
                    marginBottom={{
                        default: 'sm',
                        xxl: 'lg',
                    }}
                    dangerouslySetInnerHTML={{ __html: post.title?.raw }}
                />

                {!!excerpt && (
                    <Paragraph
                        fontColor="textMedium"
                        fontType={{
                            default: 'body',
                            lg: 'large',
                        }}
                        marginBottom={{
                            default: 'md',
                            lg: 'lg',
                            xxl: 'xl',
                        }}
                        dangerouslySetInnerHTML={{ __html: excerpt }}
                    />
                )}
            </Box>

            {/* The <div> is needed to put image on its own row, and center the image. */}
            {!!post.featuredImage &&
                (!!mediumImage || !!largeImage || !!fullImage) && (
                    <Box sx={{ textAlign: 'center' }}>
                        <Box
                            as="picture"
                            sx={{
                                'img, source': {
                                    borderRadius: '$radii.xxl',
                                    overflow: 'hidden',
                                    marginBottom: '$spacing.xs',
                                    aspectRatio: '16 / 9',
                                    objectFit: 'cover',
                                    objectPosition: 'center',
                                    height: 'auto',
                                    width: '100%',
                                },
                            }}
                        >
                            {!!mediumImage && (
                                <SourcePreload
                                    srcSet={mediumImage?.sourceUrl}
                                    media="md_lte"
                                    width={mediumImage.width}
                                    height={mediumImage.height}
                                />
                            )}
                            {!!largeImage && (
                                <ImagePreload
                                    src={
                                        largeImage?.sourceUrl ||
                                        mediumImage?.sourceUrl
                                    }
                                    alt={
                                        post.featuredImage.altText ||
                                        post.featuredImage.caption?.raw ||
                                        post.title?.raw
                                    }
                                    width={largeImage.width}
                                    height={largeImage.height}
                                />
                            )}
                            {!largeImage && !!fullImage && (
                                <ImagePreload
                                    src={fullImage.sourceUrl}
                                    width={fullImage.width}
                                    height={fullImage.height}
                                    alt={
                                        post.featuredImage.altText ||
                                        post.featuredImage.caption?.raw ||
                                        post.title?.raw
                                    }
                                />
                            )}
                        </Box>
                    </Box>
                )}

            {!!post.featuredImage?.caption?.raw && (
                <Box sx={{ '@media $xxl': { textAlign: 'center' } }}>
                    <Text
                        as="p"
                        fontColor="textLight"
                        fontType="bodySmall"
                        dangerouslySetInnerHTML={{
                            __html: post.featuredImage.caption.raw,
                        }}
                    />
                </Box>
            )}
        </React.Fragment>
    );
};

export default Hero;
